import { makeAutoObservable, observable } from 'mobx'

import { create } from '../mobx-persist'

interface CurrentPlanDetails {
  coveredByHealthInsurance: boolean
  effectiveDate: string | null
  quoteType: string
  anticipatedEndDate: string
  msPlan: any
  maPlan: any
}

export type CurrentPlanDTO = Partial<CurrentPlanDetails> &
  Required<Pick<CurrentPlanDetails, 'effectiveDate' | 'quoteType'>>

const hydrate = create({
  storage: sessionStorage,
  jsonify: true,
})

class CurrentPlanStore {
  @observable anticipatedEndDate = ''
  @observable coveredByHealthInsurance: boolean | undefined = undefined
  @observable effectiveDate: string = ''
  @observable monthlyPremium = ''
  @observable provider = ''
  @observable planType = ''
  @observable quoteType = ''
  @observable maPlanBenefits: string[] = []
  @observable rxIncluded: boolean | null = false
  @observable isPharmacyChecked: boolean = false
  @observable isPrescriptionChecked: boolean = false
  @observable isSpousePrescriptionChecked: boolean = false
  @observable isCurrentPlanQuestionChecked: boolean = false
  @observable spouseMAPlanBenefits: string[] = []
  @observable spouseCoveredByHealthInsurance: boolean | undefined = undefined
  @observable spouseEffectiveDate: string = ''
  @observable spouseMonthlyPremium = ''
  @observable spouseProvider = ''
  @observable spousePlanType = ''
  @observable spouseQuoteType = ''
  @observable spouseRxIncluded: boolean | null = false
  @observable spouseIsPharmacyChecked: boolean = false
  @observable spouseIsCurrentPlanQuestionChecked: boolean = false
  @observable spouseOptionalQuotes: string[] = []
  @observable optionalQuotes: string[] = []

  constructor() {
    makeAutoObservable(this)
  }

  setSpouseCoveredByHealthInsurance(response: boolean) {
    this.spouseCoveredByHealthInsurance = response
  }

  setSpouseEffectiveDate(response: string | null) {
    if (response) this.spouseEffectiveDate = response
    else this.spouseEffectiveDate = ''
  }

  setSpousePlanType(response: string) {
    this.spousePlanType = response
  }

  setSpouseProvider(response: string) {
    this.spouseProvider = response
  }

  setSpouseQuoteType(response: string) {
    this.spouseQuoteType = response
  }

  getIsSpousePharmacyChecked(): boolean {
    return this.spouseIsPharmacyChecked
  }

  setSpouseIsPharmacyChecked(isPharmacyChecked: boolean) {
    this.spouseIsPharmacyChecked = isPharmacyChecked
  }

  getSpouseIsCurrentPlanQuestionChecked(): boolean {
    return this.spouseIsCurrentPlanQuestionChecked
  }

  setSpouseIsCurrentPlanQuestionChecked(isCurrentPlanQuestionChecked: boolean) {
    this.spouseIsCurrentPlanQuestionChecked = isCurrentPlanQuestionChecked
  }

  setAnticipatedEndDate(response: string) {
    this.anticipatedEndDate = response
  }

  setCoveredByHealthInsurance(response: boolean) {
    this.coveredByHealthInsurance = response
  }

  setEffectiveDate(response: string | null) {
    if (response) this.effectiveDate = response
    else this.effectiveDate = ''
  }

  setPlanType(response: string) {
    this.planType = response
  }

  setProvider(response: string) {
    this.provider = response
  }

  setQuoteType(response: string) {
    this.quoteType = response
  }

  getIsPharmacyChecked(): boolean {
    return this.isPharmacyChecked
  }

  setIsPharmacyChecked(isPharmacyChecked: boolean) {
    this.isPharmacyChecked = isPharmacyChecked
  }

  getIsCurrentPlanQuestionChecked(): boolean {
    return this.isCurrentPlanQuestionChecked
  }

  setIsCurrentPlanQuestionChecked(isCurrentPlanQuestionChecked: boolean) {
    this.isCurrentPlanQuestionChecked = isCurrentPlanQuestionChecked
  }

  setMABenefits(value: string[]): void {
    this.maPlanBenefits = value
  }

  setSpouseMABenefits(value: string[]): void {
    this.spouseMAPlanBenefits = value
  }

  getMABenefits(): string[] {
    return this.maPlanBenefits
  }

  getSpouseMABenefits(): string[] {
    return this.spouseMAPlanBenefits
  }

  setOptionalQuotes(value: string[]): void {
    this.optionalQuotes = value
  }

  setSpouseOptionalQuotes(value: string[]): void {
    this.spouseOptionalQuotes = value
  }

  getOptionalQuotes(): string[] {
    return this.optionalQuotes
  }

  getSpouseOptionalQuotes(): string[] {
    return this.spouseOptionalQuotes
  }

  setIsPrescriptionChecked(value: boolean) {
    console.log('value', value)
    this.isPrescriptionChecked = value
  }

  getIsPrescriptionChecked() {
    return this.isPrescriptionChecked
  }

  setIsSpousePrescriptionChecked(value: boolean) {
    this.isSpousePrescriptionChecked = value
  }

  getIsSpousePrescriptionChecked() {
    return this.isSpousePrescriptionChecked
  }

  get() {
    return {
      anticipatedEndDate: this.anticipatedEndDate,
      coveredByHealthInsurance: this.coveredByHealthInsurance,
      effectiveDate: this.effectiveDate,
      planType: this.planType,
      provider: this.provider,
      monthlyPremium: this.monthlyPremium,
      quoteType: this.quoteType,
      rxIncluded: this.rxIncluded,
    }
  }

  private resetData = () => {
    this.coveredByHealthInsurance = undefined
    this.quoteType = ''
    this.effectiveDate = ''
    this.anticipatedEndDate = ''
    this.provider = ''
    this.planType = ''
    this.monthlyPremium = ''
    this.rxIncluded = null
    this.maPlanBenefits = []
    this.optionalQuotes = []

    this.spouseCoveredByHealthInsurance = undefined
    this.spouseQuoteType = ''
    this.spouseEffectiveDate = ''
    this.spouseProvider = ''
    this.spousePlanType = ''
    this.spouseMonthlyPremium = ''
    this.spouseRxIncluded = null
    this.spouseMAPlanBenefits = []
    this.spouseOptionalQuotes = []
  }

  reset = () => {
    this.resetData()
  }

  set = (currentPlanObj: CurrentPlanDTO | null) => {
    if (currentPlanObj) {
      this.effectiveDate = currentPlanObj.effectiveDate || ''
      this.quoteType = currentPlanObj.quoteType
      if (currentPlanObj.anticipatedEndDate)
        this.anticipatedEndDate = currentPlanObj.anticipatedEndDate
      if (currentPlanObj.msPlan) {
        this.coveredByHealthInsurance = true
        this.monthlyPremium = (
          Number(currentPlanObj.msPlan.mSupplementRate.month) / 100
        ).toFixed(2)
        this.rxIncluded = null
      } else if (currentPlanObj.maPlan) {
        this.coveredByHealthInsurance = true
        this.monthlyPremium = Number(
          currentPlanObj.maPlan.monthlyPremium
        ).toFixed(2)
        this.rxIncluded = currentPlanObj.maPlan.rxIncluded
      } else {
        this.resetData()
      }
    } else {
      this.resetData()
    }
  }
}

const currentPlanStore = new CurrentPlanStore()

export default currentPlanStore
